import React, { useEffect, useRef, useState } from "react";
import {
  Award,
  Star,
  Trophy,
  Medal,
  Crown,
  Shield,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { BiCertification } from "react-icons/bi";
import axios from "axios";

const iconMap = {
  Leadership: <Trophy className="w-6 h-6 text-amber-600" />,
  Architecture: <Award className="w-6 h-6 text-amber-600" />,
  Design: <Medal className="w-6 h-6 text-amber-600" />,
  Sustainability: <Crown className="w-6 h-6 text-amber-600" />,
  Innovation: <Shield className="w-6 h-6 text-amber-600" />,
  Quality: <Star className="w-6 h-6 text-amber-600" />,
  Certification: <BiCertification className="w-6 h-6 text-amber-600" />,
};

const bgColors = [
  "bg-gradient-to-br from-amber-50 to-orange-50",
  "bg-gradient-to-br from-orange-50 to-amber-50",
  "bg-gradient-to-br from-amber-50 via-white to-orange-50",
];

// Cycled per-card so ratings look realistic instead of a uniform 5 stars.
// If your API ever returns a real `award.rating`, that takes priority (see getRating).
const RATING_POOL = [4.5, 4.8, 4.3, 4.9, 4.6, 4.7, 4.4];

const getRating = (award, index) => {
  if (typeof award.rating === "number") return award.rating;
  return RATING_POOL[index % RATING_POOL.length];
};

const CARD_WIDTH = 320; // px, must match the card's fixed width below
const CARD_GAP = 32; // px, must match the track's gap
const STEP = CARD_WIDTH + CARD_GAP;
const AUTO_SCROLL_SPEED = 0.5; // px per tick — smaller = slower marquee

const AwardsRecognition = () => {
  const [awards, setAwards] = useState([]);
  const [offset, setOffset] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const offsetRef = useRef(0);

  useEffect(() => {
    fetchAwards();
  }, []);

  const fetchAwards = async () => {
    const res = await axios.get("https://archpoint.onrender.com/api/awards");
    setAwards(res.data);
  };

  // Continuous auto-scroll. Cards are rendered twice back-to-back, so once
  // we've scrolled exactly one full set's width we can silently jump back
  // to 0 — the duplicated set makes the wrap invisible, so it never runs
  // out of cards or shows a blank gap.
  useEffect(() => {
    if (!awards.length || isPaused) return;
    const setWidth = awards.length * STEP;

    const interval = setInterval(() => {
      offsetRef.current += AUTO_SCROLL_SPEED;
      if (offsetRef.current >= setWidth) {
        offsetRef.current -= setWidth;
      }
      setOffset(offsetRef.current);
    }, 16);

    return () => clearInterval(interval);
  }, [awards, isPaused]);

  const shift = (dir) => {
    if (!awards.length) return;
    const setWidth = awards.length * STEP;
    let next = offsetRef.current + dir * STEP;
    if (next < 0) next += setWidth;
    if (next >= setWidth) next -= setWidth;
    offsetRef.current = next;
    setOffset(next);
  };

  if (!awards.length) return null;

  const loopedAwards = [...awards, ...awards];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white py-24 px-6">
      {/* Section Header */}
      <div className="text-center mb-16">
        <span className="inline-block text-xs font-semibold tracking-widest uppercase text-amber-600 mb-3">
          Recognized Excellence
        </span>
        <h1 className="text-5xl font-bold text-slate-900 tracking-tight">
          Awards &amp;{" "}
          <span className="bg-gradient-to-r from-amber-600 to-orange-500 bg-clip-text text-transparent">
            Recognition
          </span>
        </h1>
        <div className="w-20 h-1 bg-gradient-to-r from-amber-600 to-orange-500 mx-auto mt-6 rounded-full" />
      </div>

      <div
        className="relative max-w-6xl mx-auto px-4 sm:px-16"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        {/* py-2 gives hover-lifted cards and their shadows room to breathe instead of clipping */}
        <div className="overflow-hidden py-2">
          <div
            className="flex"
            style={{
              gap: `${CARD_GAP}px`,
              transform: `translateX(-${offset}px)`,
              transition: "transform 0.05s linear",
            }}
          >
            {loopedAwards.map((award, i) => (
              <div
                key={`${award._id}-${i}`}
                className="shrink-0"
                style={{ width: `${CARD_WIDTH}px` }}
              >
                <AwardCard
                  award={award}
                  bg={bgColors[i % 3]}
                  rating={getRating(award, i)}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Edge fade so cards don't feel like they cut off abruptly */}
        <div className="pointer-events-none absolute inset-y-0 left-0 w-12 bg-gradient-to-r from-slate-50 to-transparent hidden sm:block" />
        <div className="pointer-events-none absolute inset-y-0 right-0 w-12 bg-gradient-to-l from-slate-50 to-transparent hidden sm:block" />

        {/* Arrows sit in the side padding, outside the card track, so they never cover a card */}
        <button
          onClick={() => shift(-1)}
          aria-label="Previous award"
          className="hidden sm:flex absolute left-0 top-1/2 -translate-y-1/2 bg-white border border-amber-100 shadow-md p-3 rounded-full hover:bg-amber-50 hover:border-amber-200 hover:shadow-lg transition-all duration-200 z-10"
        >
          <ChevronLeft className="w-5 h-5 text-slate-700" />
        </button>
        <button
          onClick={() => shift(1)}
          aria-label="Next award"
          className="hidden sm:flex absolute right-0 top-1/2 -translate-y-1/2 bg-white border border-amber-100 shadow-md p-3 rounded-full hover:bg-amber-50 hover:border-amber-200 hover:shadow-lg transition-all duration-200 z-10"
        >
          <ChevronRight className="w-5 h-5 text-slate-700" />
        </button>
      </div>
    </div>
  );
};

// Renders full / half / empty stars for a given numeric rating (e.g. 4.5).
const StarRating = ({ rating }) => {
  const stars = [0, 1, 2, 3, 4].map((i) => {
    const diff = rating - i;
    let fillPercent = 0;
    if (diff >= 1) fillPercent = 100;
    else if (diff > 0) fillPercent = Math.round(diff * 100);

    return (
      <span key={i} className="relative inline-block w-4 h-4">
        <Star className="absolute inset-0 w-4 h-4 text-slate-200 fill-current" />
        <span
          className="absolute inset-0 overflow-hidden"
          style={{ width: `${fillPercent}%` }}
        >
          <Star className="w-4 h-4 text-amber-500 fill-current" />
        </span>
      </span>
    );
  });

  return (
    <div className="flex items-center gap-1">
      <div className="flex gap-0.5">{stars}</div>
      <span className="ml-1.5 text-sm font-semibold text-slate-700">
        {rating.toFixed(1)}
      </span>
    </div>
  );
};

const AwardCard = ({ award, bg, rating }) => (
  <div className="group bg-white rounded-2xl border border-slate-100 shadow-sm hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 overflow-hidden h-full flex flex-col">

    {/* Image Section */}
    <div className={`relative h-72 ${bg} flex items-center justify-center shrink-0`}>
      <img
        src={award.image.url}
        alt={award.title}
        className="max-h-full max-w-full object-contain transition-transform duration-300 group-hover:scale-[1.03]"
      />

      {/* Category Icon (top-right) */}
      <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm p-2.5 rounded-full shadow-sm border border-amber-100">
        {iconMap[award.category] || <Award className="w-6 h-6 text-amber-600" />}
      </div>

      {/* CATEGORY CHIP (BOTTOM-LEFT ON IMAGE) */}
      <div className="absolute bottom-4 left-4 bg-gradient-to-r from-amber-600 to-orange-600 text-white text-xs font-semibold tracking-wide uppercase px-4 py-1.5 rounded-full shadow-md">
        {award.category}
      </div>
    </div>

    {/* Content */}
    <div className="p-6 flex-1 flex flex-col">
      {/* Title */}
      <h3 className="text-xl font-bold leading-snug text-slate-900 group-hover:text-amber-700 transition-colors duration-200">
        {award.title}
      </h3>

      {/* Rating */}
      {/* <div className="flex items-center mt-3 pt-3 border-t border-slate-100">
        <StarRating rating={rating} />
      </div> */}
    </div>
  </div>
);

export default AwardsRecognition;